import { useEffect, useRef } from 'react';

import { Button } from '@/components/primitives/Button';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

import type { TourStepDisplay } from '@/modules/tour/config/restorationTourSteps';

interface TourStepCardProps {
  display: TourStepDisplay;
  /** 1-based; omitted for the welcome, which shows the step count instead. */
  stepNumber?: number;
  stepCount: number;
  canGoBack: boolean;
  /** Move focus to the primary button, for cards that interrupt rather than point. */
  focusOnMount?: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

/**
 * Content shared by every step. Fills its container edge to edge and owns the
 * clipping, so the one-off pulse can extend past the container.
 */
export function TourStepCard({
  display,
  stepNumber,
  stepCount,
  canGoBack,
  focusOnMount = false,
  onNext,
  onBack,
  onSkip,
}: TourStepCardProps) {
  const { t } = useTranslation();
  const primary = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (focusOnMount) primary.current?.focus();
  }, [focusOnMount]);

  return (
    <div className="relative" data-testid="tour-step">
      <span
        aria-hidden
        className="tour-step-pulse pointer-events-none absolute -inset-px rounded-2xl"
      />
      <div className="flex flex-col gap-2 overflow-hidden rounded-2xl p-6">
        {stepNumber !== undefined && (
          <p className="text-xs font-medium uppercase text-brand">
            {t('Step {current} of {total}', { current: stepNumber, total: stepCount })}
          </p>
        )}
        <p className="text-lg font-semibold leading-6 text-foreground">{t(display.title)}</p>
        <p className="text-sm text-foreground-muted">{t(display.body)}</p>
        <div className="mt-6 flex items-center justify-between gap-2">
          {stepNumber === undefined ? (
            <p className="text-xs text-foreground-muted">
              {t('{count} short steps', { count: stepCount })}
            </p>
          ) : (
            <div aria-hidden className="flex gap-1.5">
              {Array.from({ length: stepCount }, (_, index) => (
                <span
                  key={index}
                  className={cn(
                    'size-1.5 rounded-full',
                    index + 1 === stepNumber ? 'bg-foreground' : 'bg-outline'
                  )}
                />
              ))}
            </div>
          )}
          <div className="flex gap-2">
            {display.welcome ? (
              <Button
                appearance="transparent"
                kind="brand"
                scale="compact"
                type="button"
                onClick={onSkip}
              >
                {t('Skip')}
              </Button>
            ) : (
              <Button
                appearance="outline"
                kind="neutral"
                scale="compact"
                type="button"
                onClick={onBack}
                disabled={!canGoBack}
              >
                {t('Back')}
              </Button>
            )}
            <Button
              appearance="solid"
              kind="inverse"
              scale="compact"
              type="button"
              onClick={onNext}
              ref={primary}
            >
              {t(display.nextLabel)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
